import { CellValueChangedEvent } from '@ag-grid-community/core';
import { Injectable } from '@angular/core';

import { ActivityType } from '@shared/services/gql.service';

import { QuarterCloseAdjustmentGridData } from '../quarter-close-adjustments.model';

@Injectable()
export class ImaTaskCalculationManagerService {
  rowsToCalculate: CellValueChangedEvent<QuarterCloseAdjustmentGridData>[] = [];

  private editableVendorEstimateColIds: (keyof QuarterCloseAdjustmentGridData)[] = [
    'vendor_estimate_amount',
    'vendor_estimate_percent_ltd',
    'vendor_estimate_amount_ltd',
  ];
  private taskTimeout = 500;
  private taskTimeoutId: NodeJS.Timeout | null = null;

  addRowToCalculate(row: CellValueChangedEvent<QuarterCloseAdjustmentGridData>) {
    this.rowsToCalculate.push(row);
  }

  /**
   * Processes a batch of row calculations and updates the grid data accordingly.
   *
   * @param calculateRow - A function that calculates new values for a given row event.
   * @param getActivitiesEffectedByVendor - A function that retrieves activity IDs affected by a vendor estimate change.
   * @param updateGridData - A function that updates the grid data with the newly calculated rows and additional rows to calculate.
   */
  calculateRows(
    calculateRow: (
      event: CellValueChangedEvent<QuarterCloseAdjustmentGridData>
    ) => QuarterCloseAdjustmentGridData[],
    getActivitiesEffectedByVendor: (activityType: ActivityType, vendorId: string) => string[],
    updateGridData: (
      newList: QuarterCloseAdjustmentGridData[],
      additionalRowsToCalculate: Set<string>
    ) => void
  ) {
    this.wrapTaskIntoTimeout(() => {
      let effectedRowsSet = new Set<string>();
      const rowsToCalculate: QuarterCloseAdjustmentGridData[] = [];
      const calculatedRows = new Set<string>();

      const sortedRowsToCalculate = this.rowsToCalculate.sort(
        (a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0)
      );

      const startEvent = sortedRowsToCalculate[0];

      if (!startEvent) {
        return;
      }

      const startIndex = startEvent.rowIndex || 0;
      const endIndex = sortedRowsToCalculate[sortedRowsToCalculate.length - 1].rowIndex || 0;
      const colField = startEvent.colDef.field;

      sortedRowsToCalculate[0].api.forEachNodeAfterFilter((node) => {
        if (
          node.rowIndex &&
          node.rowIndex >= startIndex &&
          node.rowIndex <= endIndex &&
          node.data &&
          (node.data.cost_category_name !== 'Discount' ||
            colField === 'vendor_estimate_amount_ltd' ||
            colField === 'vendor_estimate_amount')
        ) {
          const rowToCalculate = sortedRowsToCalculate.find(
            (item) => item.rowIndex === node.rowIndex
          );

          if (!rowToCalculate) {
            return;
          }

          const newList = calculateRow({
            colDef: {
              field: colField,
            },
            data: node.data,
            newValue: rowToCalculate?.newValue ?? 0,
            oldValue: rowToCalculate?.oldValue ?? 0,
          } as CellValueChangedEvent<QuarterCloseAdjustmentGridData>);

          rowsToCalculate.push(...newList);
          calculatedRows.add(node.data.activity_id);

          if (
            this.editableVendorEstimateColIds.includes(
              colField as keyof QuarterCloseAdjustmentGridData
            )
          ) {
            const effectedRows = new Set(
              getActivitiesEffectedByVendor(node.data.activity_type, node.data.organization_id)
            );

            effectedRowsSet = new Set([...effectedRows, ...effectedRowsSet]);
          }
        }
      });

      // Remove already calculated rows from the set of affected rows
      calculatedRows.forEach((id) => effectedRowsSet.delete(id));

      updateGridData(rowsToCalculate, effectedRowsSet);
    });
  }

  private clearRowsToCalculate() {
    this.rowsToCalculate = [];
  }

  wrapTaskIntoTimeout(fn: () => void) {
    if (this.taskTimeoutId) {
      clearTimeout(this.taskTimeoutId);
    }

    this.taskTimeoutId = setTimeout(() => {
      fn();
      this.taskTimeoutId = null;
      this.clearRowsToCalculate();
    }, this.taskTimeout);
  }
}
